"use client";

import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import Image from "next/image";
import { useCallback } from "react";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import {
  useInsuranceConnectorSend,
  useInsuranceConnectorState,
  useNavButtonRef,
} from "@frontend/components/InsuranceConnector/insurance-connector-provider";
import { InsurancesStatusEnum } from "@frontend/lib/graphql/generated-gql";

export interface InsuranceStatusButtonProps {
  className?: string;
}

export function InsuranceStatusButton({
  className,
}: InsuranceStatusButtonProps) {
  const insurances = useBoundStore.use.insurances();
  const navButtonRef = useNavButtonRef();

  const setButtonRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (!navButtonRef) return;
      if (node && (node.offsetWidth > 0 || node.offsetHeight > 0)) {
        navButtonRef.current = node;
      }
    },
    [navButtonRef],
  );

  let send: ReturnType<typeof useInsuranceConnectorSend> | null = null;
  let machineState: ReturnType<typeof useInsuranceConnectorState> | null = null;
  try {
    send = useInsuranceConnectorSend();
    machineState = useInsuranceConnectorState();
  } catch {
    // Provider not available, component will just show status without interaction
  }

  const t = useTranslations("InsuranceStatus");

  const hasInsurance = insurances && insurances.length > 0;
  const uiMode = machineState?.context.uiMode ?? "closed";
  const isModalOpen = uiMode !== "closed";
  const isActivelyConnecting = machineState?.context.isProcessing === true;
  const isPendingPrices = machineState?.context.isPendingPricesMode === true;
  const isUnverified =
    machineState?.context.primaryInsurance?.status ===
    InsurancesStatusEnum.Unverified; // only care about primary

  const handleClick = () => {
    if (!send) return;
    if (isModalOpen) {
      send({ type: "CLOSE" });
    } else {
      send({ type: "OPEN" });
    }
  };

  // Not Connected state
  if (!hasInsurance && !isActivelyConnecting) {
    return (
      <>
        <motion.button
          ref={setButtonRef}
          type="button"
          onClick={handleClick}
          className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-full border border-white bg-black text-white ${className || ""}`}
          aria-label={t("ConnectInsurance")}
          whileHover={{ backgroundColor: "#FFFFFF", color: "#000000" }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <Image
            src="/icons/SuperscriptCurvesIconGrey.svg"
            alt=""
            width={32}
            height={32}
            className="flex-shrink-0"
          />
          <span className="text-sm font-medium">{t("NotConnected")}</span>
        </motion.button>
        <button
          type="button"
          onClick={handleClick}
          className={`md:hidden ${className || ""}`}
          aria-label={t("ConnectInsurance")}
        >
          <Image
            src="/icons/SuperscriptCurvesIconGrey.svg"
            alt=""
            width={32}
            height={32}
          />
        </button>
      </>
    );
  }

  // Connecting state (machine is processing insurance verification)
  if (isActivelyConnecting) {
    return (
      <>
        <button
          ref={setButtonRef}
          type="button"
          className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-full bg-white text-black ${className || ""}`}
          aria-label={t("InsuranceConnecting")}
          disabled
        >
          <div className="relative flex-shrink-0">
            <Image
              src="/icons/SuperscriptCurvesIcon.svg"
              alt=""
              width={32}
              height={32}
            />
          </div>
          <span className="text-sm font-medium">{t("Connecting")}</span>
        </button>
        <button
          type="button"
          className={`md:hidden ${className || ""}`}
          aria-label={t("InsuranceConnecting")}
          disabled
        >
          <div className="relative">
            <Image
              src="/icons/SuperscriptCurvesIcon.svg"
              alt=""
              width={32}
              height={32}
            />
            <Loader2 className="h-3 w-3 text-white animate-spin absolute -top-1 -right-1" />
          </div>
        </button>
      </>
    );
  }

  // If unverified insurance but no pending prices mode show unverified:
  // waiting on design before doing this
  // if (isUnverified && !isPendingPrices) {
  //   return (
  //     <>
  //       <motion.button
  //         ref={setButtonRef}
  //         type="button"
  //         className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-full border border-white bg-black text-white ${className || ""}`}
  //         aria-label={t("InsuranceConnected")}
  //         initial={{ scale: 1 }}
  //         whileHover={{ scale: 1.05 }}
  //         whileTap={{ scale: 0.95 }}
  //         onClick={handleClick}
  //       >
  //         <div className="relative flex-shrink-0">
  //           <Image
  //             src="/icons/SuperscriptCurvesIcon.svg"
  //             alt=""
  //             width={32}
  //             height={32}
  //           />
  //           <div className="absolute -top-0 -right-0 h-2 w-2 bg-yellow-500 rounded-full" />
  //         </div>
  //         <span className="text-sm font-medium">{t("Unverified")}</span>
  //       </motion.button>
  //       <motion.button
  //         type="button"
  //         onClick={handleClick}
  //         className={`md:hidden ${className || ""}`}
  //         aria-label={t("InsuranceConnected")}
  //         initial={{ scale: 1 }}
  //         whileHover={{ scale: 1.05 }}
  //         whileTap={{ scale: 0.95 }}
  //       >
  //         <div className="relative">
  //           <Image
  //             src="/icons/SuperscriptCurvesIcon.svg"
  //             alt=""
  //             width={32}
  //             height={32}
  //           />
  //           <div
  //             className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full"
  //             style={{
  //               backgroundColor: isPendingPrices ? "#eab308" : "#22c55e",
  //             }}
  //           />
  //         </div>
  //       </motion.button>
  //     </>
  //   );
  // }

  if (isPendingPrices) {
    return (
      <>
        <motion.button
          ref={setButtonRef}
          type="button"
          className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-full border border-white bg-black text-white ${className || ""}`}
          aria-label={t("InsuranceConnected")}
          initial={{ scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleClick}
        >
          <div className="relative flex-shrink-0">
            <Image
              src="/icons/SuperscriptCurvesIcon.svg"
              alt=""
              width={32}
              height={32}
            />
            <div className="absolute -top-0 -right-0 h-2 w-2 bg-yellow-500 rounded-full" />
          </div>
          <span className="text-sm font-medium">{t("Connected")}</span>
        </motion.button>
        <motion.button
          type="button"
          onClick={handleClick}
          className={`md:hidden ${className || ""}`}
          aria-label={t("InsuranceConnected")}
          initial={{ scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className="relative">
            <Image
              src="/icons/SuperscriptCurvesIcon.svg"
              alt=""
              width={32}
              height={32}
            />
            <div
              className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full"
              style={{
                backgroundColor: isPendingPrices ? "#eab308" : "#22c55e",
              }}
            />
          </div>
        </motion.button>
      </>
    );
  }

  // Connected state
  return (
    <>
      <motion.button
        ref={setButtonRef}
        type="button"
        onClick={handleClick}
        className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-full border border-white bg-black text-white ${className || ""}`}
        aria-label={t("InsuranceConnected")}
        initial={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className="relative flex-shrink-0">
          <Image
            src="/icons/SuperscriptCurvesIcon.svg"
            alt=""
            width={32}
            height={32}
          />
          <div className="absolute -top-0 -right-0 h-2 w-2 bg-green-500 rounded-full" />
        </div>
        <span className="text-sm font-medium">{t("Connected")}</span>
      </motion.button>
      <motion.button
        type="button"
        onClick={handleClick}
        className={`md:hidden ${className || ""}`}
        aria-label={t("InsuranceConnected")}
        initial={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <div className="relative">
          <Image
            src="/icons/SuperscriptCurvesIcon.svg"
            alt=""
            width={32}
            height={32}
          />
          <div className="absolute -top-0 -right-0 h-2 w-2 bg-green-500 rounded-full" />
        </div>
      </motion.button>
    </>
  );
}
